import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, Resolver, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import format from "date-fns/format";

import { Typography, Grid, Box, useTheme, useMediaQuery } from "@mui/material";

import {
  GetPricedCustomizedPolicyQueryVariables,
  SaveCustomizedPolicyMutation,
  UnitLevelCoverages,
  useGetPricedCustomizedPolicyLazyQuery,
} from "@covertree/new-business-graphql";
import {
  PolicyCustomizationForm,
  getDefaultValuesForCustomization,
  policyCustomizationValidations,
} from "@covertree/new-business-forms";
import {
  extraCoverageAccessors,
  mapFormStateToCustomizationPayload,
} from "@covertree/new-business-mappings";
import {
  getDiscountsFromUnitPricing,
  selectCurrentlySelectedQuotePricing,
  selectCurrentOfferPricing,
  selectCustomizationOptions,
  selectIsBookConversion,
  selectPolicy,
  selectPolicyBackendLoading,
  selectPolicyStateFromAddress,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import {
  Button,
  CoverageEffectiveDate,
  Form,
  PaymentSummary,
  PaymentSummaryPricing,
  StickyContainer,
} from "@covertree/ui";
import { sortBasedOnAnotherArr, Toast } from "@covertree/utils";

import { CoveragesCustomization } from "./CoveragesCustomization";
import { ExtraCoveragesCustomization } from "./ExtraCoveragesCustomization";
import { getPackageCoveragesToRender, prepareDataToRender } from "./utils";

const OTHER_STRUCTURES_PATH = "units.0.unitLevelCoverages.coverageB_value";
const MINE_SUBSIDENCE_SCHEDULED_PATH =
  "units.0.unitLevelCoverages.mineSubsidenceScheduled_value";

interface PolicyCustomizationProps {
  goBackAndClearCache(shouldClearCache: boolean): void;
  onSaveCustomizedPolicy(
    payload: Omit<GetPricedCustomizedPolicyQueryVariables, "policyLocator">
  ): Promise<SaveCustomizedPolicyMutation["saveCustomizedPolicy"]>;
  onRemoveRevisionDependentCoverages(
    freshUnitLevelCoverages: UnitLevelCoverages
  ): void;
  useNewOffersAndSelectedQuotePricing: boolean;
  isD2C?: boolean;
}
export function PolicyCustomization({
  isD2C,
  useNewOffersAndSelectedQuotePricing,
  goBackAndClearCache,
  onSaveCustomizedPolicy,
  onRemoveRevisionDependentCoverages,
}: PolicyCustomizationProps) {
  const [canBeSaved, setCanBeSaved] = useState<boolean>();
  const [isMineSubsidenceBlocked, setIsMineSubsidenceBlocked] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isSaveLoading = useNewBusinessSelector(selectPolicyBackendLoading);
  const policy = useNewBusinessSelector(selectPolicy)!;

  const [
    getPricedCustomizedPolicy,
    {
      data: pricingResultData,
      previousData: previousPricingResult,
      loading: isPriceLoading,
    },
  ] = useGetPricedCustomizedPolicyLazyQuery();

  const pricingResult = useMemo(
    () =>
      pricingResultData?.getPricedCustomizedPolicy ||
      previousPricingResult?.getPricedCustomizedPolicy,
    [pricingResultData, previousPricingResult]
  );

  const loading = useMemo(
    () => isSaveLoading || isPriceLoading,
    [isSaveLoading, isPriceLoading]
  );

  const onSubmit = useCallback(
    async (
      formValues: PolicyCustomizationForm,
      shouldBeSaved = false
    ): Promise<boolean> => {
      const payload = mapFormStateToCustomizationPayload(formValues);

      if (shouldBeSaved) {
        try {
          const startDateBeforeChanges = policy.startDate;
          const policyAfterChanges = await onSaveCustomizedPolicy(payload);
          if (pricingResultData) {
            onRemoveRevisionDependentCoverages(
              policyAfterChanges.units[0].unitLevelCoverages
            );
          }
          goBackAndClearCache(
            startDateBeforeChanges !== policyAfterChanges.startDate
          );
        } catch (e) {
          console.error(e);
          Toast.showError("Something went wrong");
          return false;
        }
      } else {
        const { error, data } = await getPricedCustomizedPolicy({
          variables: { ...payload, policyLocator: policy.policyLocator },
        });

        if (error || !data?.getPricedCustomizedPolicy) {
          console.error(error);
          Toast.showError("Something went wrong");
          return false;
        }
        // FYI We need to reset formState because isDirty is used to enable/disable confirm button
        reset(formValues, {
          keepValues: true,
          keepErrors: true,
          keepTouched: true,
          keepDirty: false,
        });
      }
      return true;
    },
    [policy, pricingResult]
  );

  const unitPricing = useNewOffersAndSelectedQuotePricing
    ? useNewBusinessSelector(selectCurrentlySelectedQuotePricing)
    : useNewBusinessSelector(selectCurrentOfferPricing);
  const { startDate, policyLevelCoverages, units } =
    useNewBusinessSelector(selectPolicy)!;
  const isBookConversion = useNewBusinessSelector(selectIsBookConversion);
  const stateCode = useNewBusinessSelector(selectPolicyStateFromAddress);
  const {
    policyLevelCoverages: policyLevelCoveragesOptions,
    unitsWithCoverages: unitsWithCoveragesOptions,
  } = useNewBusinessSelector(selectCustomizationOptions);

  const defaultValues = useMemo(
    () =>
      getDefaultValuesForCustomization(
        startDate,
        policyLevelCoverages,
        units,
        pricingResult?.revisionDependentAccessors || [],
        unitsWithCoveragesOptions!
      ),
    [startDate, policyLevelCoverages, units, pricingResult]
  );

  const form = useForm({
    defaultValues,
    mode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(
      policyCustomizationValidations(
        policyLevelCoveragesOptions,
        unitsWithCoveragesOptions?.[0]?.unitLevelCoverages,
        isBookConversion
      )
    ) as Resolver<PolicyCustomizationForm>,
  });
  const {
    control,
    handleSubmit: submit,
    reset,
    formState,
    trigger,
    watch,
    setValue,
  } = form;

  const handleSubmit = useCallback(
    async (formValues: PolicyCustomizationForm) => {
      const succeeded = await onSubmit(formValues, !formState.isDirty);
      if (succeeded) setCanBeSaved(true);
    },
    [onSubmit, formState]
  );

  const handleCancel = useCallback(() => {
    reset();
    goBackAndClearCache(true);
  }, [reset, goBackAndClearCache]);

  const formattedStartDate = useMemo(
    () =>
      format(
        new Date(`${pricingResult?.effectiveDate || startDate} 00:00:00`),
        "MMMM do, yyyy"
      ),
    [startDate, pricingResult]
  );

  const pricing: PaymentSummaryPricing = useMemo(() => {
    const pricing = pricingResult?.pricing || unitPricing;
    const { discounts, discountsTotal } = getDiscountsFromUnitPricing(pricing);

    if (!pricing) {
      return {
        discounts: [],
        totalCost: 0,
        summary: [],
      };
    }

    return {
      discounts,
      totalCost: pricing.totalDue,
      summary: [
        {
          name: "Starting Premium",
          value: pricing.grossPremium + discountsTotal,
        },
        {
          name: "Fees",
          value: pricing.grossFees,
        },
        {
          name: "Taxes",
          value: pricing.grossTaxes,
        },
      ],
    };
  }, [unitPricing, pricingResult]);

  const coveragesToRender = useMemo(
    () =>
      prepareDataToRender(
        defaultValues,
        unitsWithCoveragesOptions?.[0]?.unitLevelCoverages,
        policyLevelCoveragesOptions,
        pricingResult?.revisionDependentAccessors
      ),
    [
      defaultValues,
      unitsWithCoveragesOptions,
      policyLevelCoveragesOptions,
      pricingResult,
    ]
  );

  useEffect(() => {
    // NOTE: This useEffect provides revalidating dependent fields on any change
    const subscription = watch(async (_, { name }) => {
      if (
        name !== "effectiveDate" &&
        !name?.includes("secondaryResidenceLiability_value")
      )
        await trigger();
    });
    return () => subscription.unsubscribe();
  }, [defaultValues, watch, trigger]);

  const otherStructuresValue = watch(OTHER_STRUCTURES_PATH);
  const mineSubsidenceScheduledValue = watch(MINE_SUBSIDENCE_SCHEDULED_PATH) as
    | unknown[]
    | undefined
    | null;

  useEffect(() => {
    // Mine Subsidence Scheduled coverages require a non-zero Other Structures limit. When
    // Other Structures is set to 0, deselect them and warn the user.

    if (otherStructuresValue === 0) {
      if (!!mineSubsidenceScheduledValue) {
        setValue(MINE_SUBSIDENCE_SCHEDULED_PATH, null, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }

      setIsMineSubsidenceBlocked(true);
    } else {
      setIsMineSubsidenceBlocked(false);
    }
  }, [otherStructuresValue, mineSubsidenceScheduledValue, setValue]);

  return (
    <Form
      onSubmit={submit((data) => handleSubmit(data as PolicyCustomizationForm))}
      id="customize-plan-form">
      <FormProvider {...form}>
        <Grid
          container
          spacing={2}
          direction={{ xs: "column-reverse", md: "row" }}
          sx={{
            marginLeft: {
              xs: 0,
              md: 2,
            },
            width: {
              xs: "100%",
              md: "auto",
            },
          }}>
          <Grid xs={12} md={7} lg={8}>
            {!isD2C && (
              <Box mt={2}>
                <Typography variant="h3">Customize your plan</Typography>
              </Box>
            )}
            <CoverageEffectiveDate
              control={control}
              name="effectiveDate"
              disabled={isBookConversion || loading}
            />
            <CoveragesCustomization
              coverages={getPackageCoveragesToRender(coveragesToRender)}
              loading={loading}
            />
            <ExtraCoveragesCustomization
              stateCode={stateCode}
              isMineSubsidenceBlocked={isMineSubsidenceBlocked}
              coverages={coveragesToRender
                .filter(({ isExtraCoverage }) => isExtraCoverage)
                .sort((item1, item2) =>
                  sortBasedOnAnotherArr(item1.accessor, item2.accessor, [
                    ...extraCoverageAccessors,
                  ])
                )}
              loading={loading}
            />
          </Grid>
          <Grid
            xs={12}
            md={5}
            lg={4}
            sx={{
              mt: { xs: isD2C ? 4 : 0, md: 0 },
              pl: { xs: 0, md: 2 },
            }}>
            <StickyContainer
              topOffset={isD2C ? 16 : 120}
              isD2C={isD2C}
              sx={{
                mt: { md: isD2C ? 3 : 0 },
              }}>
              <PaymentSummary
                pricing={pricing}
                formattedStartDate={formattedStartDate}>
                <Box
                  mt={2}
                  sx={{
                    position: { xs: "fixed", md: "static" },
                    bottom: { xs: 0, md: "auto" },
                    left: { xs: 0, md: "auto" },
                    right: { xs: 0, md: "auto" },
                    padding: { xs: 2, md: 0 },
                    backgroundColor: "background.paper",
                    width: "100%",
                    zIndex: 10,
                  }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "row", md: "column" },
                      gap: 2,
                    }}>
                    <Button
                      fullWidth
                      size={isMobile ? "small" : "medium"}
                      type="submit"
                      loading={loading}
                      disabled={
                        !formState.isValid ||
                        loading ||
                        !(formState.isDirty || canBeSaved)
                      }>
                      {formState.isDirty ? "Check price" : "Save"}
                    </Button>
                    <Button
                      fullWidth
                      size={isMobile ? "small" : "medium"}
                      type="button"
                      onClick={handleCancel}
                      variant={isMobile ? "outlined" : "text"}
                      disabled={loading}
                      data-testid="cancel-customized-plan-button">
                      Cancel
                    </Button>
                  </Box>
                </Box>
              </PaymentSummary>
            </StickyContainer>
          </Grid>
        </Grid>
      </FormProvider>
    </Form>
  );
}
